import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../design/brainTheme";
import {
  getProjectJiraIssues,
  listProjectActions,
  type BrainProject,
  type ProjectAction,
  type ProjectJiraIssuesResponse,
} from "../lib/brain-api";
import { type ActionRegisterPerson } from "./ActionRegisterTable";
import { ProjectRegister } from "./ProjectRegister";
import { buildRegisterRows, registerCounts } from "./registerRows";
import { ProgressRing } from "./ProgressRing";
import { projectStatusMeta } from "./ProjectsTable";
import { StatusPill } from "./StatusPill";
import { PersonAvatar } from "./PersonAvatar";

const c = brainTokens.color;
const STALE_MS = 14 * 24 * 60 * 60 * 1000;

function isStale(updatedAt?: number): boolean {
  if (!updatedAt) return false;
  const ms = updatedAt < 1e12 ? updatedAt * 1000 : updatedAt;
  return Date.now() - ms > STALE_MS;
}

function staleDays(updatedAt?: number): number {
  if (!updatedAt) return 0;
  const ms = updatedAt < 1e12 ? updatedAt * 1000 : updatedAt;
  return Math.round((Date.now() - ms) / (24 * 60 * 60 * 1000));
}


export function FocusProjectCard({
  project,
  owner,
  fixVersion,
  nzDate,
  people = [],
  canEdit,
}: {
  project: BrainProject;
  /** Registry owner (from getProject). Falls back to project.owner. */
  owner?: string;
  fixVersion?: string;
  nzDate: string;
  people?: ActionRegisterPerson[];
  canEdit: boolean;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState<ProjectAction[] | null>(null);
  const [jira, setJira] = useState<ProjectJiraIssuesResponse | null>(null);

  // Drill-in fetch: load the register only when the card is first expanded.
  // Jira degrades open — an unscoped or unreachable project still shows its
  // brain actions rather than an error.
  const loadRegister = useCallback(async () => {
    if (actions !== null) return;
    setLoading(true);
    const [nextActions, nextJira] = await Promise.all([
      listProjectActions({ project: project.projectId }).catch((e) => {
        enqueueSnackbar(e instanceof Error ? e.message : "Failed to load actions.", { variant: "error" });
        return [] as ProjectAction[];
      }),
      getProjectJiraIssues(project.projectId).catch(() => null),
    ]);
    setActions(nextActions);
    setJira(nextJira);
    setLoading(false);
  }, [actions, project.projectId, enqueueSnackbar]);

  useEffect(() => {
    if (open) void loadRegister();
  }, [open, loadRegister]);

  // The ring and the counters use the IN-FLIGHT set — a project with 98 open
  // issues and 12 in flight must not read as 12% done.
  const rows = useMemo(
    () => (actions === null ? [] : buildRegisterRows({ issues: jira?.issues, actions })),
    [actions, jira],
  );
  const counts = useMemo(() => registerCounts(rows), [rows]);
  const bandOrder = useMemo(() => (jira?.scopes ?? []).map((s) => s.name), [jira]);
  const done = counts.done;
  const openN = counts.inFlight;
  const total = counts.visible;
  const approxOpen = project.openActions ?? 0;
  const pct = actions !== null ? counts.pct : 0;

  const ownerName = owner?.trim() || project.owner?.trim() || "";
  // Everyone with an in-flight row — Jira assignees included, not just actions.
  const assignees = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (r.done || r.backlog) continue;
      const who = r.assignee?.trim();
      if (who && who.toLowerCase() !== ownerName.toLowerCase()) set.add(who);
    }
    return [...set].sort((x, y) => x.localeCompare(y));
  }, [rows, ownerName]);

  const cust = project.customerName ?? project.customerId;
  const isInternal = !project.customerId || project.customerId === "aeronet";
  const status = projectStatusMeta(project.status);
  const stale = isStale(project.updatedAt);
  const jiraTarget = project.jiraTarget;
  const ver = fixVersion?.trim();

  return (
    <Box
      sx={{
        background: c.pageBg,
        border: `1px solid ${c.border}`,
        borderRadius: "12px",
        boxShadow: brainTokens.shadow.card,
        overflow: "hidden",
      }}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "auto 1fr", md: "auto 1fr auto" },
          gap: { xs: 1.5, md: 2.5 },
          alignItems: "center",
          width: "100%",
          p: "18px 20px",
          m: 0,
          border: "none",
          background: "none",
          textAlign: "left",
          color: "inherit",
          font: "inherit",
          cursor: "pointer",
          "&:hover": { background: c.canvas },
        }}
      >
        <ProgressRing pct={pct} label={actions !== null ? `${pct}%` : "—"} />

        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", gap: 0.6, alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
            <Box
              component="span"
              sx={{
                fontSize: 11.5,
                fontWeight: 650,
                px: 1,
                py: 0.2,
                borderRadius: `${brainTokens.radius.pill}px`,
                background: isInternal ? c.neutralPillBg : c.tealTint,
                color: isInternal ? c.muted : c.tealInk,
              }}
            >
              {isInternal ? "Internal" : "Customer"}
              {cust ? ` · ${cust}` : ""}
            </Box>
            <StatusPill label={status.word} tone={status.tone} />
            {ver && (
              <Box
                component="span"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 650,
                  px: 1,
                  py: 0.2,
                  borderRadius: `${brainTokens.radius.pill}px`,
                  background: c.canvas,
                  border: `1px solid ${c.border}`,
                  color: c.muted,
                  fontFamily: brainTokens.font.mono,
                }}
              >
                {ver}
              </Box>
            )}
            {stale && (
              <Box
                component="span"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 650,
                  px: 1,
                  py: 0.2,
                  borderRadius: `${brainTokens.radius.pill}px`,
                  background: c.warnBg,
                  color: c.warnText,
                }}
              >
                verify — {staleDays(project.updatedAt)}d stale
              </Box>
            )}
          </Box>
          <Box sx={{ fontSize: 17, fontWeight: 650, color: c.ink, mb: 0.35, lineHeight: 1.3 }}>
            {project.name}
          </Box>
          <Box sx={{ fontSize: 13, color: c.muted }}>
            {actions !== null ? (
              <>
                <Box component="b" sx={{ color: c.ink, fontWeight: 650 }}>{openN}</Box> in flight ·{" "}
                <Box component="b" sx={{ color: c.ink, fontWeight: 650 }}>{done}</Box> done · {total} rows
                {counts.backlog > 0 && ` · ${counts.backlog} backlog`}
              </>
            ) : (
              <>
                <Box component="b" sx={{ color: c.ink, fontWeight: 650 }}>{approxOpen}</Box> open actions
              </>
            )}
            {/* Every scope the project claims — a project can span several boards. */}
            {bandOrder.length > 1 ? (
              <Box component="span" sx={{ fontFamily: brainTokens.font.mono }}>
                {" "}· {bandOrder.length} workstreams
              </Box>
            ) : jiraTarget ? (
              <Box component="span" sx={{ fontFamily: brainTokens.font.mono }}> · Jira {jiraTarget}</Box>
            ) : null}
          </Box>
        </Box>

        {/* Owner (large) + assignees (small) — replaces next-action / star / register pill */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.75,
            minWidth: 120,
          }}
        >
          {ownerName ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4 }}>
              <PersonAvatar name={ownerName} size={44} />
              <Box sx={{ fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: c.muted }}>
                Owner
              </Box>
              <Box sx={{ fontSize: 13, fontWeight: 650, color: c.ink }}>{ownerName}</Box>
            </Box>
          ) : (
            <Box sx={{ fontSize: 12, color: c.faint }}>No owner</Box>
          )}
          {assignees.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 160 }}>
              {assignees.slice(0, 6).map((who) => (
                <Box key={who} title={who}>
                  <PersonAvatar name={who} size={24} />
                </Box>
              ))}
              {assignees.length > 6 && (
                <Box sx={{ fontSize: 11, color: c.muted, alignSelf: "center" }}>+{assignees.length - 6}</Box>
              )}
            </Box>
          )}
          <Box
            component="span"
            aria-hidden
            sx={{
              fontSize: 12,
              color: c.teal,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          >
            ⌄
          </Box>
        </Box>
      </Box>

      {open && (
        <Box sx={{ borderTop: `1px solid ${c.border}`, px: 1.5, py: 1.5, background: c.canvas }}>
          {loading || actions === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <ProjectRegister
              projectId={project.projectId}
              projectName={project.name}
              issues={jira?.issues}
              actions={actions}
              bandOrder={bandOrder}
              truncated={jira?.truncated}
              nzDate={nzDate}
              people={people}
              canQuickAdd={canEdit}
              canEdit={canEdit}
              defaultReleaseVersion={ver}
              onActionsChange={setActions}
              onIssuesChange={(next) =>
                setJira((cur) => (cur ? { ...cur, issues: next } : cur))
              }
            />
          )}
        </Box>
      )}
    </Box>
  );
}
